'use client';

import { CUSTOMER_WORKFLOW } from '@/config/dashboardWorkflows';
import { useDashboardData } from '@/hooks/useDashboardData';
import { useDashboardDragDrop } from '@/hooks/useDashboardDragDrop';
import { CustomerCardType, mapToCustomerCard, type CustomerCard } from '@/utils/cardMappers';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import DashboardColumn, { DashboardCardData } from './DashboardColumn';
import StarsBackground from './StarsBackground';
import TaskModal from './TaskModal';

type SegmentType = 'audience' | 'pipeline' | 'accounts';

interface SegmentMetrics {
  metric1: string;
  metric1Label: string;
  metric2: string;
  metric2Label: string;
}

// Card type configurations with labels and source values
const cardTypeConfig: Record<CustomerCardType, { label: string; source: string; cardType: number }> = {
  subscriber: { label: 'Subscriber', source: 'newsletter', cardType: 10 },
  registrant: { label: 'Registrant', source: 'event', cardType: 10 },
  demo: { label: 'Demo Request', source: 'demo', cardType: 10 },
  trial: { label: 'Trial User', source: 'trial', cardType: 10 },
  lead: { label: 'Lead', source: 'outbound', cardType: 10 },
  referral: { label: 'Referral', source: 'referral', cardType: 10 },
  inquiry: { label: 'Inquiry', source: 'website', cardType: 10 },
  account: { label: 'Account', source: '', cardType: 9 },
};

// Use centralized config from dashboardWorkflows
const segmentConfigs = CUSTOMER_WORKFLOW.segments;

function CustomerCardView({ card, onCardClick }: { card: CustomerCard; onCardClick?: (taskId: string) => void }) {
  const borderColors = {
    amber: 'border-l-amber-500',
    emerald: 'border-l-emerald-500',
    blue: 'border-l-blue-500',
    purple: 'border-l-purple-500',
    pink: 'border-l-pink-500',
    cyan: 'border-l-cyan-500',
    slate: 'border-l-slate-400',
  };

  const typeConfig: Record<CustomerCardType, { label: string; icon: string; badge: string; borderColor: keyof typeof borderColors }> = {
    subscriber: { label: 'Subscriber', icon: '✉', badge: 'bg-slate-500/20 text-slate-300', borderColor: 'slate' },
    registrant: { label: 'Registrant', icon: '📅', badge: 'bg-purple-500/20 text-purple-300', borderColor: 'purple' },
    demo: { label: 'Demo Request', icon: '🎯', badge: 'bg-amber-500/20 text-amber-300', borderColor: 'amber' },
    trial: { label: 'Trial User', icon: '🧪', badge: 'bg-cyan-500/20 text-cyan-300', borderColor: 'cyan' },
    lead: { label: 'Lead', icon: '◎', badge: 'bg-orange-500/20 text-orange-300', borderColor: 'amber' },
    referral: { label: 'Referral', icon: '🔗', badge: 'bg-pink-500/20 text-pink-300', borderColor: 'pink' },
    inquiry: { label: 'Inquiry', icon: '💬', badge: 'bg-blue-500/20 text-blue-300', borderColor: 'blue' },
    account: { label: 'Account', icon: '★', badge: 'bg-emerald-500/20 text-emerald-300', borderColor: 'emerald' },
  };

  const config = typeConfig[card.type] || typeConfig.lead;

  const getScoreColor = (score?: number) => {
    if (!score) return '';
    if (score >= 80) return 'text-emerald-400 bg-emerald-500/20';
    if (score >= 50) return 'text-amber-400 bg-amber-500/20';
    return 'text-red-400 bg-red-500/20';
  };

  return (
    <div 
      className={`bg-gray-100 rounded-lg p-2.5 border-l-4 ${borderColors[config.borderColor]} shadow-sm hover:shadow-md transition-shadow cursor-pointer`}
      onClick={() => onCardClick?.(card.taskId)}
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${config.badge}`}>
          {config.icon} {config.label}
        </span>
        <div className="flex items-center gap-1">
          {card.score !== undefined && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${getScoreColor(card.score)}`}>
              {card.type === 'account' ? 'Health' : 'Lead'}: {card.score}
            </span>
          )}
          {card.status && (
            <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-slate-500/20 text-slate-600">
              {card.status}
            </span>
          )}
        </div>
      </div>
      <div className="text-sm font-semibold text-gray-800 mb-0.5">{card.title}</div>
      {card.company && <div className="text-xs text-gray-500 mb-1.5">{card.company}</div>}
      <div className="flex items-center justify-between text-[11px] text-gray-500">
        <div className="flex items-center gap-1">
          {card.value && (
            <span className="text-emerald-600 font-semibold">{card.value}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
            </svg>
            <span className="text-gray-700 font-medium">
              {card.assignedTo?.full_name || 'me'}
            </span>
          </div>
          <span className="text-gray-400">{card.taskId}</span>
        </div>
      </div>
    </div>
  );
}

export default function CustomerDashboard() {
  const queryClient = useQueryClient();
  const { data: tasksData, isLoading: loading, error } = useDashboardData('customer');
  const { handleCardDrop: onCardDrop } = useDashboardDragDrop('customer');
  
  const [activeSegment, setActiveSegment] = useState<SegmentType>('pipeline');
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<number | undefined>();
  const [selectedCardType, setSelectedCardType] = useState<CustomerCardType | null>(null);
  const [showAddDropdown, setShowAddDropdown] = useState(false);

  const currentSegment = CUSTOMER_WORKFLOW.segments[activeSegment];

  // Map API data to Customer cards with memoization
  const tasks = useMemo(() => {
    if (!tasksData) return [];
    return tasksData.map(mapToCustomerCard);
  }, [tasksData]);

  // Compute dynamic sidebar segment metrics from task data
  const segmentMetrics = useMemo(() => {
    const audienceTasks = tasks.filter((t: CustomerCard) => t.type === 'subscriber' || t.type === 'registrant');
    const pipelineTasks = tasks.filter((t: CustomerCard) => t.type !== 'account');
    const accountTasks = tasks.filter((t: CustomerCard) => t.type === 'account');

    const pipelineValue = pipelineTasks
      .filter((t: CustomerCard) => t.stage < 4)
      .reduce((sum: number, t: CustomerCard) => sum + (t.arrPotential || 0), 0);
    const openOpps = pipelineTasks.filter((t: CustomerCard) => t.stage < 4).length;

    const activeARR = accountTasks
      .filter((t: CustomerCard) => t.stage === 1)
      .reduce((sum: number, t: CustomerCard) => sum + (t.arrPotential || 0), 0);
    const activeCount = accountTasks.filter((t: CustomerCard) => t.stage === 1).length;

    const formatCurrency = (val: number) => {
      if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
      if (val >= 1000) return `$${(val / 1000).toFixed(0)}k`;
      return `$${val}`;
    };

    return {
      audience: {
        metric1: audienceTasks.length.toLocaleString(),
        metric1Label: 'subscribers',
        metric2: `${audienceTasks.filter((t: CustomerCard) => t.stage === 0).length}`,
        metric2Label: 'new',
      },
      pipeline: {
        metric1: formatCurrency(pipelineValue),
        metric1Label: 'pipeline',
        metric2: `${openOpps}`,
        metric2Label: 'open opps',
      },
      accounts: {
        metric1: formatCurrency(activeARR),
        metric1Label: 'total ARR',
        metric2: `${activeCount}`,
        metric2Label: 'active',
      },
    };
  }, [tasks]);

  // Compute dynamic quick stats from task data
  const quickStats = useMemo(() => {
    const newTasks = tasks.filter((t: CustomerCard) => t.stage === 0).length;
    const demoTasks = tasks.filter((t: CustomerCard) => t.type === 'demo').length;
    const atRiskTasks = tasks.filter((t: CustomerCard) => t.type === 'account' && t.stage === 2).length;
    const trialTasks = tasks.filter((t: CustomerCard) => t.type === 'trial').length;
    return { newTasks, demoTasks, atRiskTasks, trialTasks };
  }, [tasks]);

  // Filter tasks by segment with memoization
  const segmentTasks = useMemo(() => {
    return tasks.filter((task: CustomerCard) => {
      if (activeSegment === 'accounts') {
        return task.type === 'account';
      } else if (activeSegment === 'audience') {
        return task.type === 'subscriber' || task.type === 'registrant';
      } else {
        // Pipeline: all non-account types
        return task.type !== 'account';
      }
    });
  }, [tasks, activeSegment]);

  // Group tasks by stage for current segment with memoization
  const groupedTasks = useMemo(() => {
    const groups: Record<number, CustomerCard[]> = {};
    currentSegment.stages.forEach(stage => {
      groups[stage.stage] = segmentTasks.filter((t: CustomerCard) => t.stage === stage.stage);
    });
    return groups;
  }, [segmentTasks, currentSegment]);

  // Calculate KPIs dynamically with memoization
  const calculatedKPIs = useMemo(() => {
    if (activeSegment === 'pipeline') {
      const pipelineValue = segmentTasks
        .filter((t: CustomerCard) => t.stage < 4)
        .reduce((sum: number, task: CustomerCard) => sum + (task.arrPotential || 0), 0);

      const openLeads = segmentTasks.filter((t: CustomerCard) => t.stage < 4).length;
      const wonTasks = segmentTasks.filter((t: CustomerCard) => t.stage === 4);
      const wonCount = wonTasks.length;
      const demoCount = segmentTasks.filter((t: CustomerCard) => t.type === 'demo').length;

      return [
        { value: `$${(pipelineValue / 1000).toFixed(0)}k`, label: 'Pipeline Value', color: 'emerald' as const },
        { value: openLeads.toString(), label: 'Open Opportunities', color: 'white' as const },
        { value: demoCount.toString(), label: 'Demos', color: 'purple' as const },
        { value: wonCount.toString(), label: 'Won', color: 'amber' as const },
      ];
    } else if (activeSegment === 'accounts') {
      const activeTasks = segmentTasks.filter((t: CustomerCard) => t.stage === 1);
      const totalARR = activeTasks.reduce((sum: number, task: CustomerCard) => sum + (task.arrPotential || 0), 0);
      const activeCount = activeTasks.length;
      const atRiskCount = segmentTasks.filter((t: CustomerCard) => t.stage === 2).length;
      const churnedARR = segmentTasks
        .filter((t: CustomerCard) => t.stage === 3)
        .reduce((sum: number, task: CustomerCard) => sum + (task.arrPotential || 0), 0);
      const totalRevenueBase = totalARR + churnedARR;
      const nrr = totalRevenueBase > 0 ? Math.round((totalARR / totalRevenueBase) * 100) : 0;

      return [
        { value: `$${(totalARR / 1000000).toFixed(1)}M`, label: 'Total ARR', color: 'emerald' as const },
        { value: activeCount.toString(), label: 'Active Accounts', color: 'white' as const },
        { value: atRiskCount.toString(), label: 'At Risk', color: 'red' as const },
        { value: `${nrr}%`, label: 'Retention', color: 'purple' as const },
      ];
    } else {
      // Audience
      const totalSubscribers = segmentTasks.length;
      const newCount = segmentTasks.filter((t: CustomerCard) => t.stage === 0).length;
      const engaged = segmentTasks.filter((t: CustomerCard) => t.stage === 2).length;
      const converted = segmentTasks.filter((t: CustomerCard) => t.stage === 3).length;
      const engagementRate = totalSubscribers > 0 ? Math.round(((engaged + converted) / totalSubscribers) * 100) : 0;

      return [
        { value: totalSubscribers.toLocaleString(), label: 'Total Subscribers', color: 'white' as const },
        { value: newCount.toString(), label: 'New', color: 'emerald' as const },
        { value: `${engagementRate}%`, label: 'Engagement Rate', color: 'purple' as const },
        { value: converted.toString(), label: 'Converted', color: 'amber' as const },
      ];
    }
  }, [segmentTasks, activeSegment]);

  const handleCardClick = (taskId: string) => {
    const task = tasks.find((t: CustomerCard) => t.taskId === taskId);
    if (task) {
      setEditingTaskId(task.taskIdNum);
      setIsTaskModalOpen(true);
    }
  };

  const handleAddNew = (cardType?: CustomerCardType) => {
    if (cardType) {
      setSelectedCardType(cardType);
    } else {
      // If no card type specified and only one option, use it
      const availableTypes = currentSegment.cardTypes;
      if (availableTypes.length === 1) {
        setSelectedCardType(availableTypes[0] as CustomerCardType);
      } else {
        setSelectedCardType(null);
      }
    }
    setEditingTaskId(undefined);
    setShowAddDropdown(false);
    setIsTaskModalOpen(true);
  };

  const handleTaskSaved = () => {
    queryClient.invalidateQueries({ queryKey: ['dashboard', 'customer'] });
    setIsTaskModalOpen(false);
    setEditingTaskId(undefined);
  };

  const handleCardDrop = (e: React.DragEvent, taskId: string, newStage: number) => {
    onCardDrop(e, taskId, newStage, tasks);
  };

  return (
    <div className="flex-1 overflow-hidden flex flex-col bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <StarsBackground />

      {/* Sidebar and Main Content Container */}
      <div className="relative z-10 flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <aside className="w-56 flex-shrink-0 p-4">
        {/* Segment Label */}
        <div className="text-xs text-slate-500 uppercase tracking-wider mb-2 px-1">Segments</div>

        {/* Segment Buttons */}
        <div className="space-y-2 mb-6">
          {Object.entries(segmentConfigs).map(([key, segment]) => {
            const metrics = segmentMetrics[key as SegmentType];
            return (
            <button
              key={key}
              onClick={() => setActiveSegment(key as SegmentType)}
              className={`w-full rounded-lg p-3 text-left transition-all ${
                activeSegment === key
                  ? 'bg-emerald-600/20 border border-emerald-500/50 shadow-lg shadow-emerald-500/10'
                  : 'bg-slate-800/50 border border-transparent hover:bg-slate-800/70'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">{segment.icon}</span>
                <span className={`font-medium ${activeSegment === key ? 'text-emerald-300' : 'text-white'}`}>
                  {segment.name}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <div>
                  <span className={`font-bold ${activeSegment === key ? 'text-emerald-400' : 'text-white'}`}>
                    {metrics.metric1}
                  </span>
                  <span className="text-slate-500 ml-1 text-xs">{metrics.metric1Label}</span>
                </div>
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {metrics.metric2} {metrics.metric2Label}
              </div>
            </button>
            );
          })}
        </div>

        {/* Quick Stats */}
        <div className="border-t border-slate-700/50 pt-4">
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-3 px-1">Quick Stats</div>
          <div className="space-y-2">
            <div className="flex items-center justify-between px-2 py-1.5 bg-slate-800/30 rounded">
              <span className="text-xs text-slate-400">New</span>
              <span className="text-sm font-semibold text-white">{quickStats.newTasks}</span>
            </div>
            <div className="flex items-center justify-between px-2 py-1.5 bg-slate-800/30 rounded">
              <span className="text-xs text-slate-400">Demos</span>
              <span className="text-sm font-semibold text-purple-400">{quickStats.demoTasks}</span>
            </div>
            <div className="flex items-center justify-between px-2 py-1.5 bg-slate-800/30 rounded">
              <span className="text-xs text-slate-400">At Risk</span>
              <span className="text-sm font-semibold text-red-400">{quickStats.atRiskTasks}</span>
            </div>
            <div className="flex items-center justify-between px-2 py-1.5 bg-slate-800/30 rounded">
              <span className="text-xs text-slate-400">Trials</span>
              <span className="text-sm font-semibold text-amber-400">{quickStats.trialTasks}</span>
            </div>
          </div>
        </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Header Bar with Dynamic KPIs */}
        <div className="relative z-50 flex items-center flex-nowrap gap-6 mb-4 p-5 bg-slate-800/40 backdrop-blur-sm border-b border-slate-700/50">
          {/* Segment Title */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-2xl">{currentSegment.icon}</span>
            <h1 className="text-xl font-semibold text-white">{currentSegment.name}</h1>
          </div>

          {/* Divider */}
          <div className="w-px h-8 bg-slate-600 flex-shrink-0"></div>

          {/* Dynamic KPIs */}
          <div className="flex items-center gap-6 flex-shrink min-w-0 flex-nowrap">
            {calculatedKPIs.map((kpi, idx) => (
              <div key={idx} className="text-center">
                <div className={`text-2xl font-bold ${
                  kpi.color === 'white' ? 'text-white' :
                  kpi.color === 'emerald' ? 'text-emerald-400' :
                  kpi.color === 'purple' ? 'text-purple-400' :
                  kpi.color === 'amber' ? 'text-amber-400' :
                  kpi.color === 'red' ? 'text-red-400' :
                  'text-white'
                }`}>
                  {kpi.value}
                </div>
                <div className="text-xs text-slate-400">{kpi.label}</div>
              </div>
            ))}
          </div>

          {/* Filters and Add Button */}
          <div className="flex items-end gap-3 ml-auto flex-shrink-0">
            <div>
              <label className="text-slate-500 text-xs block mb-1">Owner:</label>
              <div className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded px-3 py-1.5 min-w-[130px]">
                <span className="text-slate-300 text-sm flex-1">All Owners</span>
                <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            <div>
              <label className="text-slate-500 text-xs block mb-1">Type:</label>
              <div className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded px-3 py-1.5 min-w-[110px]">
                <span className="text-slate-300 text-sm flex-1">All Types</span>
                <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            {/* Add New Dropdown */}
            {currentSegment.cardTypes.length === 1 ? (
              // Single option - just a button
              <button 
                onClick={() => handleAddNew(currentSegment.cardTypes[0] as CustomerCardType)}
                className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add {cardTypeConfig[currentSegment.cardTypes[0] as CustomerCardType].label}
              </button>
            ) : (
              // Multiple options - dropdown (matching GTM pattern)
              <div className="relative z-[60]">
                <button 
                  onClick={() => setShowAddDropdown(!showAddDropdown)}
                  className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add New
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {showAddDropdown && (
                  <>
                    <div className="fixed inset-0 z-[55]" onClick={() => setShowAddDropdown(false)} />
                    <div className="absolute right-0 top-full mt-2 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-[60] py-1 min-w-[180px]">
                      {currentSegment.cardTypes.map((cardType) => {
                        const config = cardTypeConfig[cardType as CustomerCardType];
                        if (!config) {
                          return null;
                        }
                        return (
                          <button
                            key={cardType}
                            onClick={() => handleAddNew(cardType as CustomerCardType)}
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-left hover:bg-slate-700 transition-colors text-white"
                          >
                            {config.label === 'Demo Request' && '🎯'}
                            {config.label === 'Trial User' && '🧪'}
                            {config.label === 'Lead' && '◎'}
                            {config.label === 'Referral' && '🔗'}
                            {config.label === 'Inquiry' && '💬'}
                            {config.label === 'Subscriber' && '✉'}
                            {config.label === 'Registrant' && '📅'}
                            {config.label}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Workflow view - Dynamic Columns */}
        <div className="flex-1 overflow-x-auto pb-4 px-4">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-400 text-sm">Loading customer tasks...</div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-red-400 text-sm">{error instanceof Error ? error.message : 'Failed to load tasks'}</div>
          </div>
        ) : (
            <div className="flex gap-4 min-w-max">
              {currentSegment.stages.map((stage) => {
                const stageCards = groupedTasks[stage.stage] || [];
                const mappedCards: DashboardCardData[] = stageCards.map(card => ({
                  id: card.id,
                  title: card.title,
                  type: card.type,
                  taskId: card.taskId,
                  taskIdNum: card.taskIdNum,
                  assignedTo: card.assignedTo,
                }));
                return (
                  <DashboardColumn
                    key={stage.stage}
                    title={stage.label}
                    headerColor={stage.color as 'slate' | 'blue' | 'purple' | 'amber' | 'emerald' | 'red'}
                    stage={stage.stage}
                    cards={mappedCards}
                    count={stageCards.length}
                    onCardClick={handleCardClick}
                    onCardDrop={handleCardDrop}
                    onCardDragStart={(e, taskId) => {
                      e.dataTransfer.setData('taskId', taskId);
                    }}
                    renderCard={(dashCard) => {
                      const originalCard = stageCards.find(c => c.id === dashCard.id);
                      return originalCard ? <CustomerCardView card={originalCard} onCardClick={handleCardClick} /> : null;
                    }}
                  />
                );
              })}
            </div>
          )}
        </div>
      </main>
      </div>

      {/* Task Modal */}
      <TaskModal
        isOpen={isTaskModalOpen}
        onClose={() => {
          setIsTaskModalOpen(false);
          setEditingTaskId(undefined);
          setSelectedCardType(null);
        }}
        mode={editingTaskId ? 'edit' : 'create'}
        taskId={editingTaskId}
        dashboardType="customer"
        dashboardStage={editingTaskId ? tasks.find((t: CustomerCard) => t.taskIdNum === editingTaskId)?.stage : 0}
        dashboardCardType={editingTaskId 
          ? (tasks.find((t: CustomerCard) => t.taskIdNum === editingTaskId)?.type === 'account' ? 9 : 10)
          : (selectedCardType ? cardTypeConfig[selectedCardType].cardType : currentSegment.addButtonCardType)
        }
        initialSource={selectedCardType ? cardTypeConfig[selectedCardType].source : undefined}
        onTaskSaved={handleTaskSaved}
      />
    </div>
  );
}
